import React, { useState } from "react";
import moment from "moment";
import {
  LuMail,
  LuPencil,
  LuPhone,
  LuShieldCheck,
  LuTrash2,
} from "react-icons/lu";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  setEditUser,
  setPermissionUser,
  togglePermissionModal,
  toggleUserModal,
} from "../../features/users/usersSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import useConfirmPopup from "../../context/useConfirmPopup";
import { hasPermission, permission } from "../../static/permission";
import { UsersProps } from "../../types/users";
import Badge from "../ui/Badge";
import Button from "../ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import {
  EmptyState,
  LoadingState,
  TableEmptyRow,
  TableLoadingRow,
} from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  buttonSpinnerClass,
  cardClass,
  cardContentFlushClass,
  mobileCardClass,
  tableHeadCellClass,
} from "../ui/styles";

type Props = {
  users: UsersProps[];
  pageNumber: number;
  pageSize: number;
  isLoading: boolean;
  handleDeleteUser: (id: number) => void;
};

const formatDate = (value: string) =>
  moment(value).format("DD-MM-YYYY hh:mm A");

const actionSpinnerClass = cx(
  buttonSpinnerClass,
  "mr-0 border-[#113F67]/30 border-t-[#113F67]"
);

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const avatarClass =
  "h-10 w-10 flex-shrink-0 rounded-full bg-[#113F67] text-white flex items-center justify-center text-sm font-medium";

const actionGridClass: Record<number, string> = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

const statusBadge = (status: number) =>
  status === 1 ? (
    <Badge
      variant="none"
      className="bg-green-100 text-green-800 hover:bg-green-100"
    >
      સક્રિય
    </Badge>
  ) : (
    <Badge variant="none" className="bg-red-100 text-red-800 hover:bg-red-100">
      નિષ્ક્રિય
    </Badge>
  );

const UserList = ({
  users,
  pageNumber,
  pageSize,
  isLoading,
  handleDeleteUser,
}: Props) => {
  const dispatch = useAppDispatch();
  const confirmDelete = useConfirmPopup();
  const loggingUser = useAppSelector(getLoggingUser);

  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.usersUpdate
  );
  const canPermission = hasPermission(
    loggingUser?.permissions,
    permission.usersPermission
  );
  const canDelete = hasPermission(
    loggingUser?.permissions,
    permission.usersDelete
  );

  const actionCount =
    (canUpdate ? 1 : 0) + (canPermission ? 1 : 0) + (canDelete ? 1 : 0);
  const tableColSpan = 5 + (actionCount > 0 ? 1 : 0);

  const [isActionLoading, setIsActionLoading] = useState<number>(0);

  const handleEditUser = async (user: UsersProps) => {
    await dispatch(setEditUser(user));
    dispatch(toggleUserModal(true));
  };

  const handlePermissionUser = async (user: UsersProps) => {
    await dispatch(setPermissionUser(user));
    dispatch(togglePermissionModal(true));
  };

  const handleDeleteRow = async (id: number) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (confirmed) {
      setIsActionLoading(id);
      await handleDeleteUser(id);
      setIsActionLoading(0);
    }
  };

  return (
    <>
      <div className={cx(cardClass, "max-sm:hidden")}>
        <div className={cardContentFlushClass}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={tableHeadCellClass}>ક્રમ</TableHead>
                  <TableHead className={tableHeadCellClass}>યુઝર</TableHead>
                  <TableHead className={tableHeadCellClass}>સંપર્ક</TableHead>
                  <TableHead className={tableHeadCellClass}>સ્ટેટ્સ</TableHead>
                  <TableHead className={tableHeadCellClass}>તારીખ</TableHead>
                  {actionCount > 0 && (
                    <TableHead className={cx(tableHeadCellClass, "text-right")}>
                      એકશન્સ બટન
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>

              {isLoading ? (
                <TableLoadingRow colSpan={tableColSpan} />
              ) : users.length === 0 ? (
                <TableEmptyRow
                  colSpan={tableColSpan}
                  label="યુઝર ની વિગતો મળી નથી"
                />
              ) : (
                <TableBody>
                  {users.map((user, index) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {(pageNumber - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={avatarClass}>
                            {user?.name?.substring(0, 2)?.toUpperCase() || ""}
                          </div>
                          <div>
                            <div className="font-medium">
                              {user.name || "-"}
                            </div>
                            <div className="text-sm text-gray-500">
                              ID: {user.id}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5 font-medium">
                            <LuPhone className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
                            {user.phone || "-"}
                          </div>
                          <div className="flex items-center gap-1.5 text-sm text-gray-500">
                            <LuMail className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                            {user.email || "-"}
                          </div>
                        </div>
                      </TableCell>

                      <TableCell>{statusBadge(user.status)}</TableCell>

                      <TableCell className="text-sm text-gray-500">
                        {formatDate(user.created_at)}
                      </TableCell>

                      {actionCount > 0 && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isActionLoading === user.id ? (
                              <div className={actionSpinnerClass}></div>
                            ) : (
                              <>
                                {canUpdate && (
                                  <Button
                                    variant="ghost"
                                    size="iconSm"
                                    title="સંપાદિત કરો"
                                    onClick={() => handleEditUser(user)}
                                  >
                                    <LuPencil className="h-4 w-4" />
                                  </Button>
                                )}

                                {canPermission && (
                                  <Button
                                    variant="ghost"
                                    size="iconSm"
                                    title="પરમિશન"
                                    onClick={() => handlePermissionUser(user)}
                                  >
                                    <LuShieldCheck className="h-4 w-4" />
                                  </Button>
                                )}

                                {canDelete && (
                                  <Button
                                    variant="ghost"
                                    size="iconSm"
                                    title="કાઢી નાખો"
                                    className="text-red-600 hover:text-red-700"
                                    onClick={() => handleDeleteRow(user.id)}
                                  >
                                    <LuTrash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:hidden">
        {isLoading ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : users.length === 0 ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <EmptyState label="યુઝર ની વિગતો મળી નથી" />
          </div>
        ) : (
          users.map((user, index) => (
            <div key={user.id} className={mobileCardClass}>
              <div className="flex items-start justify-between gap-3 border-b p-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className={avatarClass}>
                    {user?.name?.substring(0, 2)?.toUpperCase() || ""}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium break-words">
                      {user.name || "-"}
                    </div>
                    <div className="text-sm text-gray-500">ID: {user.id}</div>
                  </div>
                </div>

                {statusBadge(user.status)}
              </div>

              <div className="space-y-3 p-4 text-sm">
                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ક્રમ</span>
                  <span className="font-medium">
                    {(pageNumber - 1) * pageSize + index + 1}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>સંપર્ક</span>
                  <div className="min-w-0 break-words">
                    <div className="flex items-center justify-end gap-1.5 font-medium">
                      <LuPhone className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" />
                      {user.phone || "-"}
                    </div>
                    <div className="flex items-center justify-end gap-1.5 text-gray-500">
                      <LuMail className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                      {user.email || "-"}
                    </div>
                  </div>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>તારીખ</span>
                  <span className="text-right text-gray-500">
                    {formatDate(user.created_at)}
                  </span>
                </div>
              </div>

              {actionCount > 0 && (
                <div
                  className={cx(
                    "grid gap-2 border-t p-4",
                    actionGridClass[actionCount]
                  )}
                >
                  {isActionLoading === user.id ? (
                    <div className="col-span-full flex h-10 items-center justify-center">
                      <div className={actionSpinnerClass}></div>
                    </div>
                  ) : (
                    <>
                      {canUpdate && (
                        <Button
                          variant="outline"
                          title="સંપાદિત કરો"
                          onClick={() => handleEditUser(user)}
                        >
                          <LuPencil className="h-4 w-4" />
                        </Button>
                      )}

                      {canPermission && (
                        <Button
                          variant="outline"
                          title="પરમિશન"
                          onClick={() => handlePermissionUser(user)}
                        >
                          <LuShieldCheck className="h-4 w-4" />
                        </Button>
                      )}

                      {canDelete && (
                        <Button
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          title="કાઢી નાખો"
                          onClick={() => handleDeleteRow(user.id)}
                        >
                          <LuTrash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </>
  );
};
export default UserList;
